import { useEffect, useRef } from "react";

import type { FlowAnswers } from "@/shared/constants/flowTypes";

interface ExtractableItem {
  extracted_from_document?: boolean;
}

interface UseInsuranceReviewRedirectOptions {
  items: ExtractableItem[];
  isLoading: boolean;
  isFetching: boolean;
  answers: FlowAnswers;
  setAnswer: (key: string, value: string) => void;
  goToScreen: (id: string, answers: FlowAnswers) => void;
  reviewKey: string;
  manageKey: string;
  manageScreen: string;
  fallbackScreen: string;
}

export function useInsuranceReviewRedirect({
  items,
  isLoading,
  isFetching,
  answers,
  setAnswer,
  goToScreen,
  reviewKey,
  manageKey,
  manageScreen,
  fallbackScreen,
}: UseInsuranceReviewRedirectOptions) {
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (isLoading || isFetching) return;

    if (items.length > 0) {
      hasRedirected.current = false;

      const hasExtracted = items.some((p) => p.extracted_from_document);

      if (!hasExtracted && answers[reviewKey] !== "found") {
        goToScreen(manageScreen, {
          ...answers,
          [reviewKey]: "found",
          [manageKey]: "yes",
        });
        return;
      }

      if (answers[reviewKey] !== "found") {
        setAnswer(reviewKey, "found");
      }
    } else if (!hasRedirected.current && answers[reviewKey] !== "found") {
      hasRedirected.current = true;
      goToScreen(fallbackScreen, {
        ...answers,
        [reviewKey]: "none",
      });
    }
  }, [
    isLoading,
    isFetching,
    items,
    answers,
    setAnswer,
    goToScreen,
    reviewKey,
    manageKey,
    manageScreen,
    fallbackScreen,
  ]);

  const willRedirect = !isLoading && items.length === 0 && answers[reviewKey] !== "found";

  return { willRedirect };
}
